import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  untracked,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { CardModule } from '@openng/optimus-ui/card';
import { ChartModule } from '@openng/optimus-ui/chart';
import { TableModule } from '@openng/optimus-ui/table';
import { TagModule } from '@openng/optimus-ui/tag';
import { ButtonModule } from '@openng/optimus-ui/button';
import { SkeletonModule } from '@openng/optimus-ui/skeleton';
import { ChartOptions, PeriodOption, TagSeverity } from '../../models/dashboard.types';
import { BackofficeDashboardService } from '../../services/dashboard.service';
import { ThemeService } from '../../services/theme.service';
import { UiStateService } from '../../services/ui-state.service';
import { DateRangeSelectorComponent, DateRange } from '../shared/date-range-selector.component';
import { ErrorBannerComponent } from '../shared/error-banner.component';

@Component({
  selector: 'app-dashboard',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    CardModule,
    ChartModule,
    TableModule,
    TagModule,
    ButtonModule,
    SkeletonModule,
    DateRangeSelectorComponent,
    ErrorBannerComponent,
  ],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
})
export class DashboardComponent {
  protected dashboardService = inject(BackofficeDashboardService);
  protected uiState = inject(UiStateService);
  private router = inject(Router);
  private themeService = inject(ThemeService);
  protected connectionState = this.dashboardService.connectionState;

  protected selectedPeriod = this.uiState.selectedPeriod;
  protected startDate = this.uiState.startDate;
  protected endDate = this.uiState.endDate;

  protected systemOverview = this.dashboardService.systemOverview;
  protected llmUsageMetrics = computed(
    () => this.dashboardService.llmUsageMetrics()?.metrics || [],
  );
  protected topUsers = this.dashboardService.topUsers;
  protected topOrganisations = this.dashboardService.topOrganisations;
  protected organisationActivityMinutes = this.dashboardService.organisationActivityMinutes;

  protected userActivityData = computed(() =>
    this.transformActivityData(this.dashboardService.userActivityOverTime(), 'New Users'),
  );
  protected sessionActivityData = computed(() =>
    this.transformSessionActivityData(this.dashboardService.sessionActivityOverTime()),
  );

  protected activityChartOptions = computed<ChartOptions>(() => {
    const palette = this.themeService.chartPalette();
    return {
      maintainAspectRatio: false,
      aspectRatio: 1.2,
      plugins: {
        legend: { labels: { color: palette.text } },
      },
      scales: {
        x: {
          ticks: { color: palette.textSecondary },
          grid: { color: palette.surfaceBorder },
        },
        y: {
          ticks: { color: palette.textSecondary },
          grid: { color: palette.surfaceBorder },
        },
      },
    };
  });

  constructor() {
    effect(() => {
      this.uiState.startDate();
      this.uiState.endDate();
      untracked(() => this.loadDataForPeriod());
    });
  }

  protected loadDataForPeriod(): void {
    const { startDate, endDate } = this.uiState.getRange();
    this.dashboardService.loadData(startDate, endDate);
  }

  protected onPeriodChange(period: PeriodOption): void {
    this.uiState.setPeriod(period);
  }

  protected onDateRangeChange(dateRange: DateRange): void {
    this.uiState.setRange(dateRange.startDate, dateRange.endDate);
  }

  protected openOrg(orgId: number): void {
    this.router.navigate(['/organizations', orgId]);
  }

  protected openUserSessions(userId: number): void {
    this.uiState.setSessionUserFilter(userId);
    this.router.navigate(['/sessions']);
  }

  private transformActivityData(data: { date: Date; count: number }[], label: string) {
    if (!data || data.length === 0) return { labels: [], datasets: [{ label, data: [] }] };
    const labels = data.map((d) =>
      new Date(d.date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      }),
    );
    const counts = data.map((d) => d.count);
    return {
      labels,
      datasets: [
        {
          label: label,
          data: counts,
          fill: false,
          borderColor: this.themeService.chartPalette().primary,
          tension: 0.4,
        },
      ],
    };
  }

  private transformSessionActivityData(
    data: {
      date: Date;
      practiceSessions: number;
      liveSessions: number;
    }[],
  ) {
    if (!data || data.length === 0)
      return {
        labels: [],
        datasets: [
          { label: 'Practice', data: [] },
          { label: 'Live', data: [] },
        ],
      };
    const labels = data.map((d) =>
      new Date(d.date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      }),
    );
    const practiceData = data.map((d) => d.practiceSessions);
    const liveData = data.map((d) => d.liveSessions);
    return {
      labels,
      datasets: [
        {
          label: 'Practice Sessions',
          data: practiceData,
          fill: false,
          borderColor: this.themeService.chartPalette().primary,
          tension: 0.4,
        },
        {
          label: 'Live Sessions',
          data: liveData,
          fill: false,
          borderColor: this.themeService.chartPalette().primaryDark,
          tension: 0.4,
        },
      ],
    };
  }

  protected getTrendIcon(trend: number): string {
    if (trend > 0) return 'pi pi-arrow-up';
    if (trend < 0) return 'pi pi-arrow-down';
    return 'pi pi-minus';
  }

  protected getTrendColor(trend: number): TagSeverity {
    if (trend > 0) return 'success';
    if (trend < 0) return 'danger';
    return 'info';
  }

  protected formatCost(cost: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'EUR',
    }).format(cost);
  }

  protected formatTokens(tokens: number): string {
    if (tokens > 1_000_000) return `${(tokens / 1_000_000).toFixed(2)}M`;
    if (tokens > 1_000) return `${(tokens / 1_000).toFixed(2)}K`;
    return tokens.toString();
  }
}
